import * as Sentry from '@sentry/browser'
import {
  getComments,
  getCommentCounts,
  addComment,
  updateComment,
  deleteComment
} from '@/services/comments-api.service.js'
import { logger } from '@/utils/logger'

const getDefaultState = () => {
  return {
    comments: {},
    logByBoardId: {},
    countsByBoardId: {},
    threadsByBoardId: {},
    overlayVisibleByBoardId: {},
    activeThread: null
  }
}

const state = getDefaultState

// Mirrors the server's `ILIKE '%q%'` on the top-level body; a new top-level comment has no replies to match.
const matchesSearch = (text, q) =>
  String(text || '')
    .toLowerCase()
    .includes(q.toLowerCase())

const getters = {
  comments: (_state) => (boardId) => {
    return _state.comments[boardId] || []
  },
  commentLog: (_state) => (boardId) => {
    return _state.logByBoardId[boardId] || { comments: [], nextBefore: null, q: '', status: 'idle' }
  },
  overlayVisible: (_state) => (boardId) => {
    return Boolean(_state.overlayVisibleByBoardId[boardId])
  },
  activeThread: (_state) => (boardId) => {
    if (!_state.activeThread || _state.activeThread.boardId !== boardId) return null
    return _state.activeThread
  },
  commentCountsByPositionId: (_state) => (boardId) => {
    const counts = _state.countsByBoardId[boardId] || {}
    return new Map(Object.entries(counts).filter(([, count]) => count > 0))
  },
  threadComments: (_state) => (boardId) => {
    return _state.threadsByBoardId[boardId] || []
  }
}

const mutations = {
  setComments(_state, { boardId, comments }) {
    _state.comments[boardId] = comments
  },
  addComment(_state, { boardId, comment }) {
    if (!_state.comments[boardId]) _state.comments[boardId] = []
    _state.comments[boardId].push(comment)
  },
  updateComment(_state, { boardId, comment }) {
    const next = (row) =>
      row.id === comment.id ? { ...row, ...comment, createdAt: row.createdAt } : row
    if (!_state.comments[boardId]) _state.comments[boardId] = []
    const index = _state.comments[boardId].findIndex((row) => row.id === comment.id)
    if (index === -1) {
      _state.comments[boardId].push(comment)
    } else {
      _state.comments[boardId][index] = next(_state.comments[boardId][index])
    }
    const log = _state.logByBoardId[boardId]
    if (log?.comments) {
      const comments = log.comments.map(next).filter((row) => {
        const editedTopLevel = row.id === comment.id && !comment.parentCommentId
        if (!editedTopLevel || !log.q || matchesSearch(row.comment, log.q)) return true
        return (Number(row.replyCount) || 0) > 0
      })
      _state.logByBoardId = {
        ..._state.logByBoardId,
        [boardId]: { ...log, comments }
      }
    }
    const thread = _state.threadsByBoardId[boardId]
    if (thread) {
      _state.threadsByBoardId = {
        ..._state.threadsByBoardId,
        [boardId]: thread.map(next)
      }
    }
  },
  deleteComment(state, { boardId, commentId, preserveCounts = false }) {
    const { ids: removed, deltaByPosition, replyDeltaByParent } = collectDeleted(state, {
      boardId,
      commentId
    })
    if (state.comments[boardId]) {
      state.comments[boardId] = state.comments[boardId].filter((comment) => !removed.has(comment.id))
    }
    const log = state.logByBoardId[boardId]
    if (log?.comments) {
      const comments = log.comments.filter((comment) => !removed.has(comment.id))
      state.logByBoardId = {
        ...state.logByBoardId,
        [boardId]: {
          ...log,
          comments,
          // The cursor is the oldest loaded row. Deleting it would make the next older page 400.
          nextBefore: log.nextBefore === commentId ? (comments[0]?.id ?? null) : log.nextBefore
        }
      }
    }
    const thread = state.threadsByBoardId[boardId]
    if (thread) {
      state.threadsByBoardId = {
        ...state.threadsByBoardId,
        [boardId]: thread.filter((comment) => !removed.has(comment.id))
      }
    }
    if (!preserveCounts) {
      for (const [positionId, count] of deltaByPosition) {
        adjustCommentCount(state, { boardId, positionId, delta: -count })
      }
    }
    for (const [parentId, count] of replyDeltaByParent) {
      adjustReplyCount(state, { boardId, parentId, delta: -count })
    }
  },
  appendThreadComment(state, { boardId, comment }) {
    const active = state.activeThread
    if (!active || active.boardId !== boardId) return
    const rows = state.threadsByBoardId[boardId] || []
    if (rows.some((row) => row.id === comment.id)) return
    const parent = rows.find((row) => !row.parentCommentId)
    const parentId = parent?.id || active.commentId
    const isReply = Boolean(comment.parentCommentId) && comment.parentCommentId === parentId
    const isFirstOnNode =
      !comment.parentCommentId &&
      !parentId &&
      active.positionId &&
      comment.positionId === active.positionId
    if (!isReply && !isFirstOnNode) return
    state.threadsByBoardId = {
      ...state.threadsByBoardId,
      [boardId]: [...rows, comment]
    }
  },
  appendCommentLog(state, { boardId, comment }) {
    if (comment.parentCommentId) {
      adjustReplyCount(state, { boardId, parentId: comment.parentCommentId, delta: 1 })
      return
    }
    const current = state.logByBoardId[boardId]
    if (current?.q && !matchesSearch(comment.comment, current.q)) return
    const comments = current?.comments || []
    if (comments.some((row) => row.id === comment.id)) return
    state.logByBoardId = {
      ...state.logByBoardId,
      [boardId]: {
        comments: [...comments, comment],
        nextBefore: current?.nextBefore ?? null,
        q: current?.q || '',
        status: current?.status || 'ready'
      }
    }
  },
  setCommentLog(state, { boardId, comments, nextBefore, q, status }) {
    state.logByBoardId = {
      ...state.logByBoardId,
      [boardId]: { comments, nextBefore, q, status }
    }
  },
  ensureCommentInLog(state, { boardId, comment }) {
    const current = state.logByBoardId[boardId]
    const comments = current?.comments || []
    if (!comment?.id || comments.some((row) => row.id === comment.id)) return
    state.logByBoardId = {
      ...state.logByBoardId,
      [boardId]: {
        comments: [...comments, comment],
        nextBefore: current?.nextBefore ?? null,
        q: current?.q || '',
        status: current?.status || 'ready'
      }
    }
  },
  setOverlayVisible(state, { boardId, overlayVisible }) {
    state.overlayVisibleByBoardId = {
      ...state.overlayVisibleByBoardId,
      [boardId]: overlayVisible
    }
  },
  setActiveThread(state, activeThread) {
    state.activeThread = activeThread
  },
  setCommentCounts(state, { boardId, counts }) {
    noteCountSnapshot(boardId)
    state.countsByBoardId = {
      ...state.countsByBoardId,
      [boardId]: counts
    }
  },
  setThreadComments(state, { boardId, comments }) {
    state.threadsByBoardId = {
      ...state.threadsByBoardId,
      [boardId]: comments
    }
  },
  changeCommentCount(state, { boardId, positionId, delta }) {
    adjustCommentCount(state, { boardId, positionId, delta })
  },
  changeReplyCount(state, { boardId, parentId, delta }) {
    adjustReplyCount(state, { boardId, parentId, delta })
  }
}

// A failed delete adds back exactly these deltas, so counts changed by other writes meanwhile survive.
function collectDeleted(state, { boardId, commentId }) {
  const rows = [
    state.comments[boardId],
    state.threadsByBoardId[boardId],
    state.logByBoardId[boardId]?.comments
  ].flatMap((list) => list || [])
  const removed = new Map()
  for (const row of rows) {
    if (row.id === commentId || row.parentCommentId === commentId) removed.set(row.id, row)
  }
  const removedRows = [...removed.values()]
  const deltaByPosition = new Map()
  for (const comment of removedRows) {
    const positionId =
      comment.positionId ||
      (comment.parentCommentId
        ? rows.find((row) => row.id === comment.parentCommentId)?.positionId
        : null)
    if (!positionId) continue
    const loadedReplies = comment.parentCommentId
      ? 0
      : removedRows.filter((row) => row.parentCommentId === comment.id).length
    const extraReplies = comment.parentCommentId
      ? 0
      : Math.max(0, Number(comment.replyCount) || 0) - loadedReplies
    deltaByPosition.set(positionId, (deltaByPosition.get(positionId) || 0) + 1 + extraReplies)
  }
  const replyDeltaByParent = new Map()
  for (const comment of removedRows) {
    const parentId = comment.parentCommentId
    if (!parentId || removed.has(parentId)) continue
    replyDeltaByParent.set(parentId, (replyDeltaByParent.get(parentId) || 0) + 1)
  }
  return { ids: new Set(removed.keys()), deltaByPosition, replyDeltaByParent }
}

function adjustReplyCount(state, { boardId, parentId, delta }) {
  const log = state.logByBoardId[boardId]
  if (!log?.comments?.some((row) => row.id === parentId)) return
  state.logByBoardId = {
    ...state.logByBoardId,
    [boardId]: {
      ...log,
      comments: log.comments.map((row) =>
        row.id === parentId
          ? { ...row, replyCount: Math.max(0, (Number(row.replyCount) || 0) + delta) }
          : row
      )
    }
  }
}

// A counts response is dropped if the board's counts were loaded or written after it was requested.
const countsVersionByBoardId = new Map()
// Set only when a full count snapshot replaces the board map, so a failed add can tell that snapshot from its own delta.
const countSnapshotByBoardId = new Map()

function noteCountSnapshot(boardId) {
  countSnapshotByBoardId.set(boardId, countSnapshotVersion(boardId) + 1)
}

function countSnapshotVersion(boardId) {
  return countSnapshotByBoardId.get(boardId) || 0
}
const bumpCountsVersion = (boardId) => {
  const version = (countsVersionByBoardId.get(boardId) || 0) + 1
  countsVersionByBoardId.set(boardId, version)
  return version
}

function adjustCommentCount(state, { boardId, positionId, delta }) {
  if (!positionId || !delta) return
  bumpCountsVersion(boardId)
  const counts = { ...(state.countsByBoardId[boardId] || {}) }
  const next = (Number(counts[positionId]) || 0) + delta
  if (next > 0) counts[positionId] = next
  else delete counts[positionId]
  state.countsByBoardId = {
    ...state.countsByBoardId,
    [boardId]: counts
  }
}

// Opening or closing a thread supersedes any thread still loading.
let threadRequest = 0

// A log response is dropped when a later load for the same board started after it.
const logLoadByBoardId = new Map()

function beginLogLoad(boardId) {
  const generation = (logLoadByBoardId.get(boardId) || 0) + 1
  logLoadByBoardId.set(boardId, generation)
  return generation
}

function isLatestLogLoad(boardId, generation) {
  return logLoadByBoardId.get(boardId) === generation
}

function logLoadGeneration(boardId) {
  return logLoadByBoardId.get(boardId) || 0
}

// Log mutations replace only the rows they touch, so a row whose reference changed was written while the page loaded.
const mergeReplacedPage = ({ incoming, before, latest, keepAdded }) => {
  const latestById = new Map(latest.map((row) => [row.id, row]))
  const incomingIds = new Set(incoming.map((row) => row.id))
  const page = incoming.flatMap((row) => {
    if (!before.has(row.id)) return [row]
    const now = latestById.get(row.id)
    if (!now) return []
    return [now === before.get(row.id) ? row : now]
  })
  const added = keepAdded
    ? latest.filter((row) => !before.has(row.id) && !incomingIds.has(row.id))
    : []
  return [...page, ...added]
}

// Other writes may land while a delete is pending, so a failed delete puts back only its own rows.
const restoreDeleted = (previous, current, ids) => {
  const currentIds = new Set(current.map((row) => row.id))
  const restored = [...current]
  previous?.forEach((row, index) => {
    if (!ids.has(row.id) || currentIds.has(row.id)) return
    restored.splice(Math.min(index, restored.length), 0, row)
  })
  return restored
}

const failIfMissing = (result, message) => {
  if (result == null) throw new Error(message)
  return result
}

// A searched log includes a parent when any reply matches, which this client cannot see.
const reloadSearchedLog = (context, boardId) => {
  const q = context.state.logByBoardId[boardId]?.q
  if (!q) return
  return context.dispatch('loadCommentLog', { boardId, q })
}

const actions = {
  async loadCommentLog(context, { boardId, q, beforeId, signal } = {}) {
    const generation = beginLogLoad(boardId)
    const current = context.state.logByBoardId[boardId]
    const before = new Map((current?.comments || []).map((row) => [row.id, row]))
    const submittedQ = q ?? current?.q ?? ''
    const replace = !beforeId || (q != null && q !== current?.q)
    try {
      const params = { limit: 10 }
      if (submittedQ) params.q = submittedQ
      if (!replace && beforeId) params.beforeId = beforeId
      const page = await getComments({ boardId, params, signal })
      if (signal?.aborted) return
      if (!isLatestLogLoad(boardId, generation)) return
      if (!page || page.error != null) {
        const latestLog = context.state.logByBoardId[boardId]
        context.commit('setCommentLog', {
          boardId,
          comments: latestLog?.comments || [],
          nextBefore: latestLog?.nextBefore ?? null,
          q: latestLog?.q || '',
          status: 'error'
        })
        return
      }
      if (!replace && context.state.logByBoardId[boardId]?.q !== submittedQ) return
      const incoming = page.comments || []
      const latest = context.state.logByBoardId[boardId]?.comments || []
      const loadedIds = new Set(latest.map((row) => row.id))
      const comments = replace
        ? mergeReplacedPage({ incoming, before, latest, keepAdded: !submittedQ })
        : [...incoming.filter((row) => !loadedIds.has(row.id)), ...latest]
      context.commit('setCommentLog', {
        boardId,
        comments,
        nextBefore: page.nextBefore ?? null,
        q: submittedQ,
        status: 'ready'
      })
    } catch (e) {
      logger.error(e.message)
      Sentry?.captureException(e)
    }
  },
  async loadCommentLogThrough(context, { boardId, commentId, comment } = {}) {
    await context.dispatch('loadCommentLog', { boardId, q: '' })
    const loaded = context.state.logByBoardId[boardId]?.comments || []
    if (!commentId || loaded.some((row) => row.id === commentId)) return true
    if (comment?.id === commentId) {
      context.commit('ensureCommentInLog', { boardId, comment })
      return true
    }
    return false
  },
  async loadCommentCounts(context, { boardId } = {}) {
    if (!boardId) return
    const version = bumpCountsVersion(boardId)
    try {
      const body = await getCommentCounts({ boardId })
      if (!body || body.error || countsVersionByBoardId.get(boardId) !== version) return
      context.commit('setCommentCounts', { boardId, counts: body.counts || {} })
    } catch (e) {
      logger.error(e.message)
      Sentry?.captureException(e)
    }
  },
  async fetch(context, { boardId, q } = {}) {
    const generation = beginLogLoad(boardId)
    try {
      const comments = []
      let before = null
      do {
        const params = { limit: 50 }
        if (q) params.q = q
        if (before) params.beforeId = before
        const page = await getComments({ boardId, params })
        if (!isLatestLogLoad(boardId, generation)) return
        if (!page?.comments) break
        comments.push(...page.comments)
        before = page.nextBefore
      } while (before)
      if (!isLatestLogLoad(boardId, generation)) return
      context.commit('setComments', { boardId, comments })
    } catch (e) {
      logger.error(e.message)
      Sentry?.captureException(e)
    }
  },
  async addComment(context, { boardId, comment }) {
    const now = new Date()
    const previousThread = context.state.activeThread
    const stored = { ...comment, updatedAt: now, createdAt: now }
    context.commit('addComment', { boardId, comment: stored })
    context.commit('appendCommentLog', { boardId, comment: stored })
    context.commit('appendThreadComment', { boardId, comment: stored })
    context.commit('changeCommentCount', { boardId, positionId: comment.positionId, delta: 1 })
    const countsSnapshotAtAdd = countSnapshotVersion(boardId)
    const shouldAttachEmptyThread =
      previousThread &&
      previousThread.boardId === boardId &&
      previousThread.commentId == null &&
      previousThread.positionId &&
      previousThread.positionId === comment.positionId &&
      !comment.parentCommentId
    if (shouldAttachEmptyThread) {
      context.commit('setActiveThread', {
        ...previousThread,
        commentId: comment.id
      })
    }
    try {
      const saved = failIfMissing(
        await addComment({ boardId, comment }),
        'Failed to add comment'
      )
      context.dispatch('loadCommentCounts', { boardId })
      if (comment.parentCommentId) await reloadSearchedLog(context, boardId)
      return saved
    } catch (e) {
      // A snapshot that landed while this add was in flight already excludes it.
      const preserveCounts = countSnapshotVersion(boardId) !== countsSnapshotAtAdd
      context.commit('deleteComment', { boardId, commentId: comment.id, preserveCounts })
      const currentThread = context.state.activeThread
      const stillOnFailedComment =
        currentThread?.boardId === boardId && currentThread?.commentId === comment.id
      if (shouldAttachEmptyThread && stillOnFailedComment) {
        context.commit('setActiveThread', previousThread)
      }
      logger.error(e.message)
      Sentry?.captureException(e)
      throw e
    }
  },
  async updateComment(context, { boardId, comment }) {
    try {
      const saved = failIfMissing(
        await updateComment({ boardId, comment }),
        'Failed to update comment'
      )
      context.commit('updateComment', {
        boardId,
        comment: { ...comment, updatedAt: new Date() }
      })
      if (comment.parentCommentId) await reloadSearchedLog(context, boardId)
      return saved
    } catch (e) {
      logger.error(e.message)
      Sentry?.captureException(e)
      throw e
    }
  },
  async deleteComment(context, { boardId, commentId }) {
    const previousComments = context.state.comments[boardId]
    const previousLog = context.state.logByBoardId[boardId]
    const previousThread = context.state.threadsByBoardId[boardId]
    const previousActive = context.state.activeThread
    const { ids, deltaByPosition, replyDeltaByParent } = collectDeleted(context.state, {
      boardId,
      commentId
    })
    const deletedIsReply = [previousComments, previousThread, previousLog?.comments].some((rows) =>
      rows?.some((row) => row.id === commentId && row.parentCommentId)
    )
    context.commit('deleteComment', { boardId, commentId })
    const countsSnapshotAtDelete = countSnapshotVersion(boardId)
    const logLoadAtDelete = logLoadGeneration(boardId)
    const closedThread = previousActive?.boardId === boardId && previousActive.commentId === commentId
    if (closedThread) context.commit('setActiveThread', null)
    try {
      const removed = await deleteComment({ boardId, commentId })
      const loaded = context.state.logByBoardId[boardId]?.comments
      const exhaustedCursor = previousLog?.nextBefore === commentId && !loaded?.length
      if (exhaustedCursor) await context.dispatch('loadCommentLog', { boardId })
      await context.dispatch('loadCommentCounts', { boardId })
      if (deletedIsReply) await reloadSearchedLog(context, boardId)
      return removed
    } catch (e) {
      const currentComments = context.state.comments[boardId]
      const currentLog = context.state.logByBoardId[boardId]
      if (currentComments) {
        context.commit('setComments', {
          boardId,
          comments: restoreDeleted(previousComments, currentComments, ids)
        })
      }
      if (currentLog) {
        const restored = previousLog?.comments?.find((row) => row.id === commentId)
        let comments = restoreDeleted(previousLog?.comments, currentLog.comments, ids)
        if (currentLog.q && restored && !matchesSearch(restored.comment, currentLog.q)) {
          comments = comments.filter((row) => row.id !== commentId)
        }
        context.commit('setCommentLog', {
          boardId,
          ...currentLog,
          comments
        })
      }
      // The optimistic delete already closed the popover. A failed delete restores the rows and leaves it closed.
      const currentThread = context.state.threadsByBoardId[boardId] || []
      const previousThreadIds = new Set((previousThread || []).map((row) => row.id))
      const sameThread = currentThread.length
        ? currentThread.some((row) => previousThreadIds.has(row.id))
        : closedThread && context.state.activeThread == null
      if (sameThread) {
        context.commit('setThreadComments', {
          boardId,
          comments: restoreDeleted(previousThread, currentThread, ids)
        })
      }
      // A snapshot that landed while this delete was in flight already includes these rows.
      if (countSnapshotVersion(boardId) === countsSnapshotAtDelete) {
        for (const [positionId, count] of deltaByPosition) {
          context.commit('changeCommentCount', { boardId, positionId, delta: count })
        }
      }
      // A log load started after this delete already brought back the server reply count.
      if (logLoadGeneration(boardId) === logLoadAtDelete) {
        for (const [parentId, count] of replyDeltaByParent) {
          context.commit('changeReplyCount', { boardId, parentId, delta: count })
        }
      }
      logger.error(e.message)
      Sentry?.captureException(e)
      throw e
    }
  },
  toggleOverlay({ commit, dispatch, getters }, { boardId } = {}) {
    if (!boardId) return
    const overlayVisible = !getters.overlayVisible(boardId)
    commit('setOverlayVisible', { boardId, overlayVisible })
    if (overlayVisible) {
      dispatch('loadCommentCounts', { boardId })
    }
  },
  applyOverlay({ commit, dispatch }, { boardId, overlayVisible } = {}) {
    if (!boardId) return
    commit('setOverlayVisible', { boardId, overlayVisible: Boolean(overlayVisible) })
    if (overlayVisible) {
      dispatch('loadCommentCounts', { boardId })
    }
  },
  async loadScenarioComments({ dispatch }, { boardId } = {}) {
    if (!boardId) return
    await dispatch('loadCommentCounts', { boardId })
  },
  async openThread(
    { commit, state },
    { boardId, commentId, positionId, focusComposer, scrollToCommentId } = {}
  ) {
    const request = ++threadRequest
    const setThread = (thread) => {
      const next = scrollToCommentId ? { ...thread, scrollToCommentId } : thread
      commit('setActiveThread', focusComposer ? { ...next, focusComposer: true } : next)
    }
    const known = commentId
      ? (state.comments[boardId] || []).find((item) => item.id === commentId)
      : null
    const rootId = known?.parentCommentId || commentId || null
    if (!rootId && !positionId) {
      commit('setThreadComments', { boardId, comments: [] })
      setThread({ boardId, commentId: null, positionId: null })
      return
    }
    const params = rootId ? { commentId: rootId } : { positionId }
    const page = await getComments({ boardId, params })
    if (request !== threadRequest) return null
    if (page == null || page.error) {
      if (page?.error === 400) {
        commit('setThreadComments', { boardId, comments: [] })
        commit('setActiveThread', null)
        return false
      }
      return 'error'
    }
    const rows = page.comments || []
    const wantedId = scrollToCommentId || rootId
    if (wantedId && !rows.some((row) => row.id === wantedId)) {
      commit('setThreadComments', { boardId, comments: [] })
      commit('setActiveThread', null)
      return false
    }
    commit('setThreadComments', { boardId, comments: rows })
    const parent = rows.find((row) => !row.parentCommentId) || null
    setThread({
      boardId,
      commentId: parent?.id || rootId || null,
      positionId: positionId || known?.positionId || parent?.positionId || null
    })
    return true
  },
  closeThread({ commit }) {
    threadRequest++
    commit('setActiveThread', null)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
